import React, {type ReactElement} from "react";
import {marked} from "marked";
import {Helmet} from "react-helmet";

import {MarkdownVideoBlock} from "../markdown-video-block/MarkdownVideoBlock";

import {sanitizeMarkdown, css} from "/src/utils/utils";
import * as styles from "./markdown-text.module.scss";

marked.setOptions({
  sanitize: false,
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false,
});
marked.use({
  extensions: [MarkdownVideoBlock as marked.TokenizerAndRendererExtension],
});

const renderer = new marked.Renderer();

renderer.blockquote = (text: string) =>
  `<blockquote class="italic">${text}</blockquote>`;

renderer.codespan = (text: string) =>
  `<span class="markdown-text__highlight">${text}</span>`;

renderer.em = (text: string) => `<span class="italic">${text}</span>`;

renderer.heading = (text: string, level: number) => {
  return `<h${level} class="${css(
    `h${level}`,
    "markdownText__heading"
  )}">${text}</h${level}>`;
};

renderer.image = (src: string) =>
  `<div class="markdown-img-container">
    <img src="${src}" alt="markdown image"/>
  </div>`;

renderer.link = (href: string, title: string, text: string) => {
  const isExternalLink = !href.startsWith("#");
  const attributes = isExternalLink ? 'target="_blank"' : "";

  return `<a href="${href}" ${attributes} class="p">${text}</a>`;
};

renderer.list = (body: string, ordered: boolean) => {
  return ordered ? `<ol class="p">${body}</ol>` : `<ul class="p">${body}</ul>`;
};

renderer.listitem = (text: string) => `<li>${text}</li>`;

renderer.paragraph = (text: string) => `<p>${text}</p>`;

renderer.strong = (text: string) => `<span class="bold">${text}</span>`;

renderer.table = (header: string, body: string) =>
  `<table>
    ${header}
    ${body}
  </table>`;

export enum MARKDOWN_TYPES {
  ARTICLE = "ARTICLE",
  DEFAULT = "DEFAULT",
  LEGAL = "LEGAL",
}

interface MarkdownTextProps {
  text: string;
  classes?: string;
  type?: MARKDOWN_TYPES;
}

const MarkdownText = (props: MarkdownTextProps): ReactElement | null => {
  const {classes, text, type} = props;
  return text ? (
    <>
      {type === MARKDOWN_TYPES.LEGAL && (
        <Helmet>
          <script async src="//cdn.embedly.com/widgets/platform.js" />
        </Helmet>
      )}
      <div
        className={css(
          styles.markdownText,
          type === MARKDOWN_TYPES.ARTICLE ? styles.markdownTextArticle : "",
          type === MARKDOWN_TYPES.LEGAL ? styles.markdownTextLegal : "",
          classes!
        )}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizeMarkdown(marked(text!, {renderer})),
        }}
      />
    </>
  ) : null;
};

export default MarkdownText;
